import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig, getPrintColors } from "../../../shared/lib/systemConfig";
import { formatCustomerPaymentNo } from "../../../shared/lib/documentNumbers";
import { printHtml } from "../../../shared/lib/printHtml";
import { formatCurrency } from "../../../utils/formatters";
import { useTranslation } from "react-i18next";

/** Coerce API / query values; treat blank / null as missing (not zero). */
function toMoney(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Build previous debt / paid / remaining from API fields, with query-param
 * and arithmetic fallbacks so the receipt never shows only the paid amount.
 */
function resolveDebtSnapshot(data = {}, query = {}) {
  const payment = toMoney(
    data.payment ?? data.amount ?? data.amount_main ?? data.amount_paid ?? query.paid
  ) ?? 0;

  let oldOwed = toMoney(
    data.old_owed
    ?? data.previous_debt
    ?? data.old_debt
    ?? data.previous_owed
    ?? query.old
  );
  let remaining = toMoney(
    data.total_remaind
    ?? data.total_remaining
    ?? data.remaining
    ?? data.new_owed
    ?? data.total_owed
    ?? query.remain
  );

  if (oldOwed == null && remaining != null) {
    oldOwed = Math.round((remaining + payment) * 100) / 100;
  }
  if (remaining == null && oldOwed != null) {
    remaining = Math.round(Math.max(0, oldOwed - payment) * 100) / 100;
  }
  if (oldOwed == null) oldOwed = payment;
  if (remaining == null) remaining = 0;

  return { oldOwed, payment, remaining };
}

const moneyText = (value, code) => formatCurrency(value ?? 0, code);

const OwedReceipt = () => {
  const { t, i18n } = useTranslation();
  const { id, paymentId } = useParams();
  const [searchParams] = useSearchParams();
  const [receiptData, setReceiptData] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(Boolean(id && paymentId));
  const [systemSettings, setSystemSettings] = useState(() => readCachedSystemConfig());

  useEffect(() => {
    let cancelled = false;
    fetchSystemConfig()
      .then((config) => {
        if (!cancelled) setSystemSettings(config);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, []);

  const cfg = systemSettings ?? readCachedSystemConfig() ?? {};
  const colors = getPrintColors(cfg);
  const mainCode = cfg.currency_code || "IQD";
  const secondaryCode = cfg.secondary_currency_code || "";
  const lang = String(i18n.language || "en").split("-")[0] || "en";
  const rtl = lang === "ku" || lang === "ar";

  const companyTitle =
    (cfg.invoice_company_name || "").trim() ||
    (cfg.site_name || "").trim() ||
    t("velanFactoryName", "IQ Price");
  const tagline = (cfg.invoice_tagline || "").trim();
  const headerLogoSrc = cfg.invoice_logo_url || cfg.sidebar_logo_url || "";
  const displayEmail = (cfg.invoice_email || "").trim();
  const displayWeb = (cfg.invoice_website || "").trim();
  const phoneLines = (cfg.invoice_phones || "").trim()
    ? cfg.invoice_phones.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : [];
  const addressText = (cfg.invoice_address || "").trim();
  const footerNote = (cfg.invoice_footer_note || "").trim();

  useEffect(() => {
    const fetchPaymentReceipt = async () => {
      if (!id || !paymentId) {
        setReceiptData(null);
        setLoadingReceipt(false);
        return;
      }
      setLoadingReceipt(true);
      const queryFallback = {
        old: searchParams.get("old"),
        paid: searchParams.get("paid"),
        remain: searchParams.get("remain"),
      };
      try {
        const res = await apiClient.get(
          `/customers/${id}/payment-receipt/${paymentId}`
        );
        const data = res.data || {};
        const snap = resolveDebtSnapshot(data, queryFallback);
        setReceiptData({
          customerName: data.customer_name,
          oldOwed: snap.oldOwed,
          payment: snap.payment,
          amountMain: data.amount_main,
          amountSecondary: data.amount_secondary,
          exchangeRate: data.exchange_rate,
          totalOwed: snap.remaining,
          sender: data.sender,
          receiver: data.receiver,
          date: data.created_at,
          documentNo: data.document_no || formatCustomerPaymentNo(paymentId, data.created_at),
        });
      } catch (error) {
        const snap = resolveDebtSnapshot({}, queryFallback);
        if (queryFallback.old != null || queryFallback.paid != null || queryFallback.remain != null) {
          setReceiptData({
            customerName: searchParams.get("name") || "",
            oldOwed: snap.oldOwed,
            payment: snap.payment,
            totalOwed: snap.remaining,
            date: new Date().toISOString(),
            documentNo: formatCustomerPaymentNo(paymentId),
          });
        } else {
          setReceiptData(null);
        }
        console.error("Error fetching payment receipt:", error);
      } finally {
        setLoadingReceipt(false);
      }
    };
    fetchPaymentReceipt();
  }, [id, paymentId, searchParams]);

  const handlePrint = () => {
    const node = document.querySelector(".printable-content");
    if (!node) {
      window.print();
      return;
    }
    printHtml(
      "<!doctype html><html dir=\"" + (rtl ? "rtl" : "ltr") + "\" lang=\"" + lang + "\"><head><meta charset=\"utf-8\"><title>" +
      (receiptData?.documentNo || t("receipt", "Receipt")) +
      "</title><style>" +
      "@page{margin:10mm}" +
      "html,body{margin:0;padding:0;background:#fff;color:#111;width:100%}" +
      "body{font-family:Tahoma,\"Segoe UI\",Arial,sans-serif;font-size:14px;direction:" + (rtl ? "rtl" : "ltr") + "}" +
      ".printable-content{width:100%;max-width:100%;margin:0;padding:0;border:none!important;box-shadow:none!important;border-radius:0!important;background:#fff!important}" +
      ".debt-sheet{box-shadow:none!important;border:none!important}" +
      ".no-print{display:none!important}" +
      "img{max-width:100%;height:auto}" +
      "*{-webkit-print-color-adjust:exact;print-color-adjust:exact}" +
      "</style></head><body>" +
      node.outerHTML +
      "</body></html>"
    );
  };

  const docNo = receiptData?.documentNo || formatCustomerPaymentNo(paymentId, receiptData?.date);
  const paidDate = receiptData?.date
    ? format(new Date(receiptData.date), "dd/MM/yyyy HH:mm")
    : "";
  const remainingZero = Number(receiptData?.totalOwed ?? 0) <= 0.009;
  const contactBits = [
    ...phoneLines,
    displayEmail,
    displayWeb,
    addressText,
  ].filter(Boolean);

  return (
    <>
      {loadingReceipt && (
        <div style={{ textAlign: "center", margin: "2rem", fontWeight: 600 }}>
          {t("loading", "Loading...")}
        </div>
      )}
      {!loadingReceipt && !receiptData && (
        <div style={{ textAlign: "center", color: "#b91c1c", fontWeight: 700, margin: "2rem" }}>
          {t("receiptDataNotFound", "No receipt data found or payment not found.")}
        </div>
      )}
      {receiptData && (
        <div>
          <div
            className="printable-content debt-sheet"
            style={{
              fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
              direction: rtl ? "rtl" : "ltr",
              width: "100%",
              maxWidth: "18cm",
              margin: "24px auto",
              padding: "22px 24px 20px",
              background: "#fff",
              border: `1px solid ${colors.primary}33`,
              boxSizing: "border-box",
              color: colors.primary,
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                paddingBottom: 14,
                borderBottom: `2px solid ${colors.primary}`,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: colors.accent, lineHeight: 1.2 }}>
                  {companyTitle}
                </div>
                {tagline ? (
                  <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, opacity: 0.8 }}>
                    {tagline}
                  </div>
                ) : null}
              </div>
              {headerLogoSrc ? (
                <img
                  src={headerLogoSrc}
                  alt=""
                  style={{ width: 72, height: 72, objectFit: "contain", flexShrink: 0 }}
                />
              ) : null}
            </div>

            {/* Title + meta */}
            <div style={{ textAlign: "center", margin: "16px 0 14px" }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 900,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: colors.accent,
                }}
              >
                {t("paymentReceiptTitle", "Payment Receipt")}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4, opacity: 0.75 }}>
                {t("customerDebtPayment", "Customer debt payment")}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                marginBottom: 16,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <div>
                <span style={{ opacity: 0.65 }}>{t("receiptNumber", "Receipt Number")}</span>
                <div style={{ fontSize: 15, fontWeight: 900, color: colors.accent, marginTop: 2 }}>
                  {docNo}
                </div>
              </div>
              <div style={{ textAlign: rtl ? "left" : "right" }}>
                <span style={{ opacity: 0.65 }}>{t("paymentDateTimeLabel", "Payment Date & Time")}</span>
                <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>{paidDate || "—"}</div>
              </div>
            </div>

            {/* Customer */}
            <div
              style={{
                border: `1px solid ${colors.primary}`,
                padding: "10px 12px",
                marginBottom: 14,
                background: colors.soft,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.7 }}>
                {t("customerNameLabel", "Customer Name")}
              </div>
              <div style={{ fontSize: 17, fontWeight: 900, marginTop: 2, color: colors.accent }}>
                {receiptData.customerName || "—"}
              </div>
            </div>

            {/* Ledger */}
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 14,
                fontSize: 14,
              }}
            >
              <tbody>
                <tr>
                  <td style={{ padding: "10px 0", borderBottom: `1px solid ${colors.primary}33`, fontWeight: 700 }}>
                    {t("previousDebtLabel", "Previous Debt")}
                  </td>
                  <td
                    style={{
                      padding: "10px 0",
                      borderBottom: `1px solid ${colors.primary}33`,
                      textAlign: rtl ? "left" : "right",
                      fontWeight: 800,
                      fontFamily: '"Courier New", Tahoma, monospace',
                      direction: "ltr",
                    }}
                  >
                    {moneyText(receiptData.oldOwed, mainCode)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "12px 0", borderBottom: `1px solid ${colors.primary}33`, fontWeight: 800, color: colors.accent }}>
                    {t("paidAmountLabel", "Amount Paid")}
                  </td>
                  <td
                    style={{
                      padding: "12px 0",
                      borderBottom: `1px solid ${colors.primary}33`,
                      textAlign: rtl ? "left" : "right",
                      fontWeight: 900,
                      fontSize: 18,
                      color: colors.accent,
                      fontFamily: '"Courier New", Tahoma, monospace',
                      direction: "ltr",
                    }}
                  >
                    {moneyText(receiptData.payment, mainCode)}
                  </td>
                </tr>
                {receiptData.amountSecondary != null && Number(receiptData.amountSecondary) > 0 && secondaryCode ? (
                  <tr>
                    <td style={{ padding: "10px 0", borderBottom: `1px solid ${colors.primary}33`, fontWeight: 700, fontSize: 13 }}>
                      {t("secondaryCurrencyPaidLabel", "Payment in Secondary Currency")}
                    </td>
                    <td
                      style={{
                        padding: "10px 0",
                        borderBottom: `1px solid ${colors.primary}33`,
                        textAlign: rtl ? "left" : "right",
                        fontWeight: 700,
                        fontSize: 13,
                        fontFamily: '"Courier New", Tahoma, monospace',
                        direction: "ltr",
                      }}
                    >
                      {Number(receiptData.amountSecondary).toLocaleString()} {secondaryCode}
                      {receiptData?.exchangeRate?.main_per_secondary
                        ? ` · ${t("rateLabel", "Rate")}: 1 ${mainCode} = ${receiptData.exchangeRate.main_per_secondary} ${secondaryCode}`
                        : ""}
                    </td>
                  </tr>
                ) : null}
                <tr>
                  <td style={{ padding: "12px 0 4px", fontWeight: 900, fontSize: 15 }}>
                    {t("remainingDebtLabel", "Remaining Debt")}
                  </td>
                  <td
                    style={{
                      padding: "12px 0 4px",
                      textAlign: rtl ? "left" : "right",
                      fontWeight: 900,
                      fontSize: 18,
                      color: remainingZero ? "#15803d" : colors.accent,
                      fontFamily: '"Courier New", Tahoma, monospace',
                      direction: "ltr",
                    }}
                  >
                    {moneyText(receiptData.totalOwed, mainCode)}
                  </td>
                </tr>
              </tbody>
            </table>

            {remainingZero ? (
              <div
                style={{
                  textAlign: "center",
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  padding: "8px 10px",
                  marginBottom: 16,
                  border: `1.5px solid ${colors.primary}`,
                  color: colors.accent,
                }}
              >
                {t("debtCleared", "Account cleared — no remaining debt")}
              </div>
            ) : null}

            {/* Signatures — hidden for now
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 28,
                marginTop: 22,
                marginBottom: 18,
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 28 }}>
                  {t("receivedBy", "Received by")}
                </div>
                <div style={{ borderTop: `1px solid ${colors.primary}`, paddingTop: 6, minHeight: 22, fontSize: 13, fontWeight: 700 }}>
                  {receiptData.receiver || ""}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 28 }}>
                  {t("paidBy", "Paid by")}
                </div>
                <div style={{ borderTop: `1px solid ${colors.primary}`, paddingTop: 6, minHeight: 22, fontSize: 13, fontWeight: 700 }}>
                  {receiptData.sender || receiptData.customerName || ""}
                </div>
              </div>
            </div>
            */}

            {footerNote ? (
              <div
                style={{
                  textAlign: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: 0.85,
                  whiteSpace: "pre-wrap",
                  marginBottom: 10,
                }}
              >
                {footerNote}
              </div>
            ) : null}

            {/* Footer contact */}
            {contactBits.length > 0 ? (
              <div
                style={{
                  borderTop: `1px solid ${colors.primary}`,
                  paddingTop: 10,
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1.55,
                  opacity: 0.8,
                }}
              >
                {contactBits.join("  ·  ")}
              </div>
            ) : null}
          </div>

          <div
            style={{ position: "fixed", bottom: 40, right: 40, zIndex: 1000 }}
            className="no-print"
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              {t("print", "Print")}
            </Button>
          </div>
          <style>
            {`
              @media print {
                .no-print, .MuiButton-root { display: none !important; }
                .printable-content {
                  margin: 0 !important;
                  max-width: 100% !important;
                  border: none !important;
                  box-shadow: none !important;
                }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
};

export default OwedReceipt;
